import React, { useState } from "react";
import EditLanguageIndex from "./EditLanguage/index";
import useLanguage from "../useLanguage";
import EditImg from "../../../images/SideBar/Edit";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import Button from "../../../components/Botton";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField } from "@mui/material";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { getSortByTimeButtonText } from "../../../util/getSortByTimeText";
import { hasPermission } from "../../../utils/permissions";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/slices/authSlice";

const Language: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "language", "update");
  const canAdd = hasPermission(user, "language", "create");
  const canDelete = hasPermission(user, "language", "delete");

  const columns: GridColDef[] = [
    {
      field: "languageName",
      headerName: "Education Language",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "languageCode",
      headerName: "Education Language Code",
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center gap-3">
          {canEdit && (
            <button
              onClick={() => handleEdit(params.row._id)}
              className="text-fontColor hover:text-primary-2"
            >
              <EditImg />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => openDeleteModal(params.row._id)}
              className="text-fontColor hover:text-red-500"
            >
              <DeleteImg />
            </button>
          )}
        </div>
      ),
    });
  }

  const {
    selectedLanguageId,
    languageData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    setFilterModel,
    setSortingModel,
    paginationModel,
    setPaginationModel,
    rowCount,
    handleAdd,
    // New time sort items
    handleSortByTime,
    timeSortOrder,
  } = useLanguage(columns);

  const [isModalOpen, setIsModalOpen] = useState(false); // Modal open state
  const [languageToDelete, setLanguageToDelete] = useState<string | null>(null);

  const openDeleteModal = (id: string) => {
    setLanguageToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (languageToDelete) {
      handleDelete(languageToDelete);
      setIsModalOpen(false);
    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setLanguageToDelete(null);
  };

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedLanguageId && canEdit ? (
        <EditLanguageIndex languageId={selectedLanguageId} />
      ) : (
        <div className="space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md">
          <div className="flex justify-between items-center gap-4 mr-1 mb-4">
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Search By"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ maxWidth: '400px' }}
            />
            <div className="flex gap-4">
              <Button
                onClick={handleSortByTime}
                className="py-1.5 px-9 rounded"
                children={getSortByTimeButtonText(timeSortOrder)}
                type="button"
              />
              {canAdd && (
                <Button
                  onClick={handleAdd}
                  className="py-1.5 px-9 rounded"
                  children="Add Edu. Language"
                  type="button"
                />
              )}
            </div>
          </div>
          <CommonDataGrid
            id="language"
            rows={languageData}
            columns={columns}
            loading={loading}
            showConfirmation={false}
            confirmationMessage={""}
            setFilterModel={setFilterModel}
            setSortingModel={setSortingModel}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onCancelDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            onConfirmDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            toolbarProps={{ toolbar: { showQuickFilter: true } }}
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this language?"
      />
      <Toaster />
    </div>
  );
};

export default Language;
